// T3-B sponsored motif (PR 4, 2026-05-07; PR 4 refactor 2026-05-12):
// campaign edit sheet 内に埋め込む motif 一覧 + 追加/編集/削除 UI。
// 入稿フォームに「SNS 映えチェックリスト」を含める (= spec §6.3 2026-05-03 update)。
//
// PR 4 (2026-05-12) refactor:
//   - 5-tier hardcoded dropdown を撤廃し、tier プリセット選択 + tier_value 選択 (動的) に refactor
//   - template_motif_id 選択 (任意、= テンプレ motif sponsor 化フロー §F8) を追加
//   - status 選択 (draft/active/paused/ended) を追加
//   - 後方互換: legacy field `fortune_tier` も並列で送信 (= omikuji-api PR #117 が auto-fill 担当)
//   - `position_in_grid` 入力フィールドは v3 で廃止 (= 25 セル運用撤廃)
//
// Spec: design/sponsored-motif-spec.md v3 §6.3 / §6.8 / §F8

import { Button } from '@/components/common/Button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/common/Form';
import { Input } from '@/components/common/Input';
import { Textarea } from '@/components/common/Textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '@/components/common/Select';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { toastify } from '@/utils/toastify';
import { yupResolver } from '@hookform/resolvers/yup';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Pencil, Trash2 } from 'lucide-react';
import type { FortuneTierPreset, TemplateMotif } from '@/stores/TierCatalogStore';

// SNS 映えチェックリスト (spec §6.3 2026-05-03 update)
const SNS_CHECKLIST_ITEMS = [
    'motif 画像内に sponsor logo を含む',
    '取得場所のテキストが画像内に含まれる',
    'Instagram フィード 1:1 でクロップしても破綻しない',
    'Instagram ストーリー 9:16 でクロップしても破綻しない',
    '縦書き / 横書き 両方の SNS 投稿に対応',
    '"Sponsored by ◯◯" 表示が画像内に含まれる (= スクショで確実に保持)'
];

const STATUS_OPTIONS = [
    { value: 'draft', label: 'draft (= 下書き、配布対象外)' },
    { value: 'active', label: 'active (= 配布中)' },
    { value: 'paused', label: 'paused (= 一時停止)' },
    { value: 'ended', label: 'ended (= 期間終了)' }
];

// 数値型に変換しつつ "" / null を undefined にする transform (= yup InferType と相性が良い)。
const optionalNumber = () =>
    yup
        .number()
        .integer()
        .transform((v, o) => (o === '' || o == null ? undefined : v));

const motifSchema = yup.object({
    name: yup
        .string()
        .required('motif 名を入力してください')
        .max(200),
    imageUrl: yup
        .string()
        .required('画像 URL を入力してください')
        .max(500)
        .url('URL を入力してください'),
    tierPresetId: optionalNumber()
        .required('tier プリセットを選択してください'),
    tierValue: optionalNumber()
        .required('tier を選択してください'),
    status: yup
        .string()
        .oneOf(['draft', 'active', 'paused', 'ended'])
        .required(),
    templateMotifId: optionalNumber(),
    fortuneText: yup
        .string()
        .required('占い文を入力してください'),
    sponsorMessage: yup
        .string()
        .nullable()
        .max(200, '200 文字以下を入力してください')
        .transform((v, o) => (o === '' ? null : v)),
    spawnRate: yup
        .number()
        .typeError('数値を入力してください')
        .required('排出確率を入力してください')
        .min(0, '0〜1 の範囲で入力してください')
        .max(1, '0〜1 の範囲で入力してください'),
    couponTemplateId: optionalNumber()
});

type MotifFormValues = yup.InferType<typeof motifSchema>;

interface Props {
    campaignId: string | number,
    campaignStatus: string
}

export default observer(function MotifsSection({ campaignId, campaignStatus }: Props) {
    const {
        sponsoredMotifStore: {
            motifs,
            listByCampaign: fetchMotifs,
            create: createMotif,
            update: updateMotif,
            remove: removeMotif
        },
        tierCatalogStore: {
            tierPresets,
            templateMotifs,
            loadAll: loadCatalog
        },
        modalStore: { showAlertModal }
    } = useStore();

    const [formOpen, setFormOpen] = useState(false);
    const [editTarget, setEditTarget] = useState<any | null>(null);
    const [snsChecklist, setSnsChecklist] = useState<boolean[]>(Array(SNS_CHECKLIST_ITEMS.length).fill(false));

    const defaultPreset: FortuneTierPreset | undefined = useMemo(
        () => tierPresets.find(p => p.preset_key === 'omikuji_preset') ?? tierPresets[0],
        [tierPresets]
    );

    const form = useForm<MotifFormValues>({
        resolver: yupResolver(motifSchema) as any,
        mode: 'onSubmit',
        defaultValues: {
            name: '',
            imageUrl: '',
            tierPresetId: undefined,
            tierValue: undefined,
            status: 'draft',
            templateMotifId: undefined,
            fortuneText: '',
            sponsorMessage: '',
            spawnRate: 0.04,
            couponTemplateId: undefined
        }
    });

    const watchedPresetId = form.watch('tierPresetId');
    const selectedPreset = useMemo(
        () => tierPresets.find(p => Number(p.id) === Number(watchedPresetId)),
        [tierPresets, watchedPresetId]
    );
    const presetTierOptions = useMemo(
        () => selectedPreset?.tier_definitions ?? [],
        [selectedPreset]
    );

    useEffect(() => {
        fetchMotifs(campaignId);
        loadCatalog(false);
    }, [campaignId]);

    const handleOpenCreate = () => {
        setEditTarget(null);
        setSnsChecklist(Array(SNS_CHECKLIST_ITEMS.length).fill(false));
        const presetId = defaultPreset ? Number(defaultPreset.id) : undefined;
        const tierValue = defaultPreset?.tier_definitions?.[0]?.value ?? undefined;
        form.reset({
            name: '',
            imageUrl: '',
            tierPresetId: presetId,
            tierValue: tierValue,
            status: 'draft',
            templateMotifId: undefined,
            fortuneText: '',
            sponsorMessage: '',
            spawnRate: 0.04,
            couponTemplateId: undefined
        });
        setFormOpen(true);
    };

    const handleOpenEdit = (m: any) => {
        setEditTarget(m);
        setSnsChecklist(Array(SNS_CHECKLIST_ITEMS.length).fill(false));
        // backward compat: 旧 row は tier_preset_id NULL / fortune_tier 1-5 のみ → omikuji_preset で resolve
        const resolvedPresetId = m.tier_preset_id != null ?
            Number(m.tier_preset_id) :
            (defaultPreset ? Number(defaultPreset.id) : undefined);
        const resolvedTierValue = m.tier_value != null ?
            Number(m.tier_value) :
            (m.fortune_tier != null ? Number(m.fortune_tier) : undefined);
        form.reset({
            name: m.name ?? '',
            imageUrl: m.image_url ?? '',
            tierPresetId: resolvedPresetId,
            tierValue: resolvedTierValue,
            status: m.status ?? 'draft',
            templateMotifId: m.template_motif_id != null ? Number(m.template_motif_id) : undefined,
            fortuneText: m.fortune_text ?? '',
            sponsorMessage: m.sponsor_message ?? '',
            spawnRate: Number(m.spawn_rate ?? 0.04),
            couponTemplateId: m.coupon_template_id != null ? Number(m.coupon_template_id) : undefined
        });
        setFormOpen(true);
    };

    const handleDelete = (id: number, name: string) => {
        showAlertModal({
            title: '削除確認',
            size: 'sm',
            content: `「${name}」を削除しますか? (論理削除のため復元可能)`,
            onSave: async () => {
                const res = await flowResult(removeMotif(id));
                if (res?.ok) {
                    toastify('success', '削除しました');
                    fetchMotifs(campaignId);
                } else {
                    toastify('error', res?.message || '削除に失敗しました');
                }
            }
        });
    };

    const onSubmit = async (data: MotifFormValues) => {
        // backward compat: legacy field `fortune_tier` を omikuji_preset 限定で並列送信。
        // omikuji-api PR #117 が tier_preset_id + tier_value の primary を解釈する。
        const isOmikujiPreset = selectedPreset?.preset_key === 'omikuji_preset';
        const tierValue = data.tierValue ?? null;
        const legacyFortuneTier = isOmikujiPreset && tierValue != null && tierValue >= 1 && tierValue <= 5 ?
            tierValue :
            undefined;

        const payload: Record<string, any> = {
            name: data.name,
            image_url: data.imageUrl,
            tier_preset_id: data.tierPresetId,
            tier_value: tierValue,
            status: data.status,
            template_motif_id: data.templateMotifId ?? null,
            fortune_text: data.fortuneText,
            sponsor_message: data.sponsorMessage ?? null,
            spawn_rate: data.spawnRate,
            coupon_template_id: data.couponTemplateId ?? null
        };
        if (legacyFortuneTier !== undefined) {
            payload.fortune_tier = legacyFortuneTier;
        }

        const res = editTarget ?
            await flowResult(updateMotif({ id: editTarget.id, data: payload })) :
            await flowResult(createMotif({ campaignId, data: payload }));

        if (res?.ok) {
            toastify('success', editTarget ? '更新しました' : '追加しました');
            setFormOpen(false);
            fetchMotifs(campaignId);
        } else {
            toastify('error', res?.message || '保存に失敗しました');
        }
    };

    const checklistAllChecked = snsChecklist.every(Boolean);
    const canAdd = campaignStatus !== 'ended';

    // 表示用: motif row の tier ラベル (= 取得時に運営が見る列)
    const renderTierLabel = (m: any): string => {
        const presetId = m.tier_preset_id;
        const tierValue = m.tier_value ?? m.fortune_tier;
        if (tierValue == null) return '-';
        const preset = tierPresets.find(p => Number(p.id) === Number(presetId));
        if (preset) {
            const def = preset.tier_definitions.find(d => Number(d.value) === Number(tierValue));
            if (def) return def.label;
        }
        // fallback: 旧 fortune_tier のみある場合
        return `(legacy ${tierValue})`;
    };

    return (
        <div>
            <div className='flex items-center justify-between mb-3'>
                <h3 className='text-base font-semibold'>
                    配下 motif 一覧 (
                    {motifs.length}
                    )
                </h3>
                <Button
                    variant='outline'
                    size='sm'
                    onClick={handleOpenCreate}
                    disabled={!canAdd}
                    title={canAdd ? '' : '終了したキャンペーンには motif を追加できません'}
                >
                    motif を追加
                </Button>
            </div>

            {motifs.length === 0 ?
                (
                    <p className='text-sm text-gray-500 py-4'>まだ motif がありません</p>
                ) :
                (
                    <table className='w-full text-sm'>
                        <thead>
                            <tr className='border-b border-gray-200 text-left'>
                                <th className='py-2 px-2'>名称</th>
                                <th className='py-2 px-2'>tier</th>
                                <th className='py-2 px-2'>status</th>
                                <th className='py-2 px-2 text-right'>排出%</th>
                                <th className='py-2 px-2'>sponsor メッセージ</th>
                                <th className='py-2 px-2 w-[80px]'>操作</th>
                            </tr>
                        </thead>
                        <tbody>
                            {motifs.map(m => (
                                <tr key={m.id} className='border-b border-gray-100 hover:bg-gray-50'>
                                    <td className='py-2 px-2 font-medium'>{m.name}</td>
                                    <td className='py-2 px-2'>{renderTierLabel(m)}</td>
                                    <td className='py-2 px-2 text-xs text-gray-600'>{m.status ?? '-'}</td>
                                    <td className='py-2 px-2 text-right font-mono'>
                                        {(Number(m.spawn_rate) * 100).toFixed(1)}
                                        %
                                    </td>
                                    <td className='py-2 px-2 text-gray-600 truncate max-w-[200px]'>
                                        {m.sponsor_message ?? '-'}
                                    </td>
                                    <td className='py-2 px-2'>
                                        <div className='flex gap-2'>
                                            <Button variant='outline' size='sm' onClick={() => handleOpenEdit(m)}>
                                                <Pencil className='w-3 h-3' />
                                            </Button>
                                            <Button
                                                variant='outline'
                                                size='sm'
                                                onClick={() => handleDelete(m.id, m.name)}
                                                className='text-red-600 hover:text-red-800'
                                            >
                                                <Trash2 className='w-3 h-3' />
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}

            {formOpen && (
                <div className='mt-4 p-4 border border-gray-200 rounded bg-gray-50'>
                    <div className='flex items-center justify-between mb-3'>
                        <h4 className='text-sm font-semibold'>
                            {editTarget ? 'motif 編集' : 'motif 追加'}
                        </h4>
                        <Button variant='outline' size='sm' onClick={() => setFormOpen(false)}>
                            閉じる
                        </Button>
                    </div>

                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-3'>
                            <FormField
                                control={form.control}
                                name='name'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>
                                            motif 名
                                            <span className='text-red-500'>*</span>
                                        </FormLabel>
                                        <FormControl>
                                            <Input {...field} placeholder='例: オリオン獅子 紅型版' />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name='imageUrl'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>
                                            画像 URL
                                            <span className='text-red-500'>*</span>
                                        </FormLabel>
                                        <FormControl>
                                            <Input {...field} placeholder='https://...' />
                                        </FormControl>
                                        <FormMessage />
                                        <p className='text-xs text-gray-500 mt-1'>1024x768 PNG 推奨。スクショ前提のデザインに。</p>
                                    </FormItem>
                                )}
                            />

                            {/* v3 (PR 4): tier プリセット + tier value 選択 (動的) */}
                            <div className='grid grid-cols-2 gap-3'>
                                <FormField
                                    control={form.control}
                                    name='tierPresetId'
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>
                                                tier プリセット
                                                <span className='text-red-500'>*</span>
                                            </FormLabel>
                                            <Select
                                                value={field.value != null ? String(field.value) : ''}
                                                onValueChange={(v) => {
                                                    field.onChange(v ? Number(v) : undefined);
                                                    // preset 切替で tier_value も先頭に reset
                                                    const newPreset = tierPresets.find(p => Number(p.id) === Number(v));
                                                    const firstTier = newPreset?.tier_definitions?.[0]?.value;
                                                    if (firstTier != null) {
                                                        form.setValue('tierValue', firstTier);
                                                    }
                                                }}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue placeholder='選択してください' />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {tierPresets.map(p => (
                                                        <SelectItem key={p.id} value={String(p.id)}>
                                                            {p.name}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            <FormMessage />
                                            <p className='text-xs text-gray-500 mt-1'>
                                                両替機 PoC は omikuji_preset (大吉〜凶 5 段)
                                            </p>
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name='tierValue'
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>
                                                tier
                                                <span className='text-red-500'>*</span>
                                            </FormLabel>
                                            <Select
                                                value={field.value != null ? String(field.value) : ''}
                                                onValueChange={v => field.onChange(v ? Number(v) : undefined)}
                                                disabled={presetTierOptions.length === 0}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue placeholder={presetTierOptions.length === 0 ? '先に preset を選択' : '選択'} />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {presetTierOptions.map(def => (
                                                        <SelectItem key={def.value} value={String(def.value)}>
                                                            {def.label}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>

                            {/* v3 (PR 4): テンプレ motif sponsor 化 (任意、§F8) */}
                            <FormField
                                control={form.control}
                                name='templateMotifId'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>テンプレ motif sponsor 化 (任意)</FormLabel>
                                        <Select
                                            value={field.value != null ? String(field.value) : '__none__'}
                                            onValueChange={v => field.onChange(v === '__none__' ? undefined : Number(v))}
                                        >
                                            <SelectTrigger>
                                                <SelectValue placeholder='選択しない (完全 sponsor 独自 motif)' />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value='__none__'>選択しない (完全 sponsor 独自 motif)</SelectItem>
                                                {templateMotifs.map((tm: TemplateMotif) => (
                                                    <SelectItem key={tm.id} value={String(tm.id)}>
                                                        {`${tm.template_set_name ? `[${tm.template_set_name}] ` : ''}${tm.name}`}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <FormMessage />
                                        <p className='text-xs text-gray-500 mt-1'>
                                            テンプレ motif を選ぶと「期間中独占購入」となり、同テンプレを他 sponsor が買えなくなります。両替機 PoC 5 種は対象外。
                                        </p>
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name='status'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>
                                            status
                                            <span className='text-red-500'>*</span>
                                        </FormLabel>
                                        <Select value={field.value} onValueChange={field.onChange}>
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {STATUS_OPTIONS.map(s => (
                                                    <SelectItem key={s.value} value={s.value}>
                                                        {s.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <FormMessage />
                                        <p className='text-xs text-gray-500 mt-1'>
                                            排出対象は status=active のみ。campaign 期間 + status の両方が active 必須。
                                        </p>
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name='fortuneText'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>
                                            占い文
                                            <span className='text-red-500'>*</span>
                                        </FormLabel>
                                        <FormControl>
                                            <Textarea {...field} rows={3} placeholder='200 字程度' />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name='sponsorMessage'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>sponsor メッセージ (Layer 2、50-200 字)</FormLabel>
                                        <FormControl>
                                            <Textarea
                                                {...field}
                                                value={field.value ?? ''}
                                                rows={2}
                                                placeholder='例: 沖縄の夏に、乾杯。'
                                            />
                                        </FormControl>
                                        <FormMessage />
                                        <p className='text-xs text-gray-500 mt-1'>未入力なら Layer 2 でメッセージ枠ごと非表示</p>
                                    </FormItem>
                                )}
                            />

                            <div className='grid grid-cols-2 gap-3'>
                                <FormField
                                    control={form.control}
                                    name='spawnRate'
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>排出確率 (0-1)</FormLabel>
                                            <FormControl>
                                                <Input type='number' step='0.001' {...field} />
                                            </FormControl>
                                            <FormMessage />
                                            <p className='text-xs text-gray-500 mt-1'>0.04 = 4%</p>
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name='couponTemplateId'
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>クーポン template ID (任意)</FormLabel>
                                            <FormControl>
                                                <Input
                                                    type='number'
                                                    {...field}
                                                    value={field.value ?? ''}
                                                    placeholder='将来 FK 化予定'
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>

                            {/* SNS 映えチェックリスト (spec §6.3 2026-05-03 update) */}
                            <div className='border border-gray-300 rounded p-3 bg-white'>
                                <p className='text-sm font-medium mb-2'>SNS 映えチェックリスト (= 入稿時に sponsor が確認)</p>
                                <p className='text-xs text-gray-500 mb-2'>
                                    シェア機能撤廃方針のため、ユーザがスクショで自由に SNS 投稿できる前提でデザインを確認してください。
                                </p>
                                <div className='space-y-1'>
                                    {SNS_CHECKLIST_ITEMS.map((item, idx) => (
                                        <label key={idx} className='flex items-start gap-2 text-xs cursor-pointer'>
                                            <input
                                                type='checkbox'
                                                checked={snsChecklist[idx]}
                                                onChange={(e) => {
                                                    const next = [...snsChecklist];
                                                    next[idx] = e.target.checked;
                                                    setSnsChecklist(next);
                                                }}
                                                className='mt-0.5'
                                            />
                                            <span>{item}</span>
                                        </label>
                                    ))}
                                </div>
                                <p className='text-xs text-gray-500 mt-2'>
                                    {checklistAllChecked ?
                                        '✅ すべて確認済' :
                                        `未確認: ${snsChecklist.filter(b => !b).length} 項目`}
                                </p>
                            </div>

                            <div className='flex justify-end gap-2'>
                                <Button type='button' variant='outline' onClick={() => setFormOpen(false)}>
                                    キャンセル
                                </Button>
                                <Button type='submit' variant='active'>
                                    {editTarget ? '更新' : '追加'}
                                </Button>
                            </div>
                        </form>
                    </Form>
                </div>
            )}
        </div>
    );
});
